var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { cn } from "../../lib/utils.js";
import { cva } from "class-variance-authority";
/**
 * Layout/structure only. Color classes live in `badgeColorVariants` and are
 * applied AFTER `className` so consumer `bg-*` / `text-*` cannot silently win.
 */
var badgeVariants = cva("inline-flex items-center justify-center gap-1 rounded-2xl font-medium text-center transition-colors focus:outline-none", {
    variants: {
        variant: {
            // Base color variants
            beige: "",
            pink: "",
            orange: "",
            electricGreen: "",
            blue: "",
            green: "",
            teal: "",
            lavender: "",
            // Light variants
            beigeLight: "",
            pinkLight: "",
            orangeLight: "",
            electricGreenLight: "",
            blueLight: "",
            greenLight: "",
            tealLight: "",
            lavenderLight: "",
            // Dark variants
            beigeDark: "",
            pinkDark: "",
            orangeDark: "",
            electricGreenDark: "",
            blueDark: "",
            greenDark: "",
            tealDark: "",
            lavenderDark: "",
            // Grey variants
            greyLight: "",
            grey: "",
            greyDark: "",
            // Utility variants
            success: "",
            successMedium: "",
            successDark: "",
            warning: "",
            warningMedium: "",
            warningDark: "",
            danger: "",
            dangerMedium: "",
            dangerDark: "",
            // Disabled state
            disabled: "",
            // Default variants for backward compatibility
            default: "",
            secondary: "",
            destructive: "",
            outline: "",
        },
        size: {
            default: "px-1.5 py-0.5 text-xs font-medium leading-[15.6px]",
            sm: "px-1 py-0.5 text-[10px] font-medium",
            lg: "px-2 py-1 text-sm font-medium",
        },
    },
    defaultVariants: {
        variant: "default",
        size: "default",
    },
});
/** Forced after className (with !) so MFE color overrides cannot diverge. */
var badgeColorVariants = {
    beige: "!bg-[#FCEBDF] !text-[#242424] dark:!text-foreground dark:!bg-[#5a473a]",
    pink: "!bg-[#FEDBD7] !text-[#242424] dark:!text-foreground dark:!bg-[#5a3b3a]",
    orange: "!bg-[#FCD4C9] !text-[#242424] dark:!text-foreground dark:!bg-[#5a3f34]",
    electricGreen: "!bg-[#E3FF6A] !text-[#242424] dark:!text-foreground dark:!bg-[#485a2c]",
    blue: "!bg-[#DCE2FC] !text-[#242424] dark:!text-foreground dark:!bg-[#3a3e5a]",
    green: "!bg-[#CCE0D9] !text-[#242424] dark:!text-foreground dark:!bg-[#3a5a4d]",
    teal: "!bg-[#BAE7E8] !text-[#242424] dark:!text-foreground dark:!bg-[#365a5a]",
    lavender: "!bg-[#E9D6EE] !text-[#242424] dark:!text-foreground dark:!bg-[#4b3a5a]",
    beigeLight: "!bg-[#FAF4F0] !text-[#242424] dark:!text-foreground dark:!bg-[#453930]",
    pinkLight: "!bg-[#FFEEED] !text-[#242424] dark:!text-foreground dark:!bg-[#453131]",
    orangeLight: "!bg-[#FFE8E1] !text-[#242424] dark:!text-foreground dark:!bg-[#45352e]",
    electricGreenLight: "!bg-[#F4FFC6] !text-[#242424] dark:!text-foreground dark:!bg-[#384523]",
    blueLight: "!bg-[#EDF0FF] !text-[#242424] dark:!text-foreground dark:!bg-[#313445]",
    greenLight: "!bg-[#EAF4F1] !text-[#242424] dark:!text-foreground dark:!bg-[#31453c]",
    tealLight: "!bg-[#DCF4F5] !text-[#242424] dark:!text-foreground dark:!bg-[#2f4545]",
    lavenderLight: "!bg-[#FAF0FC] !text-[#242424] dark:!text-foreground dark:!bg-[#3c3145]",
    beigeDark: "!bg-[#D4C3BB] !text-[#242424] dark:!text-foreground dark:!bg-[#6b5747]",
    pinkDark: "!bg-[#FF9C94] !text-[#242424] dark:!text-foreground dark:!bg-[#6b4745]",
    orangeDark: "!bg-[#F2B6A4] !text-[#242424] dark:!text-foreground dark:!bg-[#6b4d3e]",
    electricGreenDark: "!bg-[#DAF565] !text-[#242424] dark:!text-foreground dark:!bg-[#586b33]",
    blueDark: "!bg-[#A4B1EB] !text-[#242424] dark:!text-foreground dark:!bg-[#434872]",
    greenDark: "!bg-[#A5C9BA] !text-[#242424] dark:!text-foreground dark:!bg-[#43705d]",
    tealDark: "!bg-[#7AC9CC] !text-[#242424] dark:!text-foreground dark:!bg-[#3d7070]",
    lavenderDark: "!bg-[#D6B4DE] !text-[#242424] dark:!text-foreground dark:!bg-[#594370]",
    greyLight: "!bg-[#F7F4F2] !text-[#242424] dark:!text-foreground dark:!bg-[#383634]",
    grey: "!bg-[#E5E1DF] !text-[#242424] dark:!text-foreground dark:!bg-[#4a4845]",
    greyDark: "!bg-[#CCC8C6] !text-[#242424] dark:!text-foreground dark:!bg-[#5c5a57]",
    success: "!bg-[#009735] !text-[#FAF4F0] dark:!text-white",
    successMedium: "!bg-[#27B85A] !text-[#FAF4F0] dark:!text-white",
    successDark: "!bg-[#006623] !text-[#FAF4F0] dark:!text-white",
    warning: "!bg-[#BD5601] !text-[#FAF4F0] dark:!text-white",
    warningMedium: "!bg-[#EC7303] !text-[#FAF4F0] dark:!text-white",
    warningDark: "!bg-[#873D00] !text-[#FAF4F0] dark:!text-white",
    danger: "!bg-[#D90B0B] !text-[#FAF4F0] dark:!text-white",
    dangerMedium: "!bg-[#F6584B] !text-[#FAF4F0] dark:!text-white",
    dangerDark: "!bg-[#9E0000] !text-[#FAF4F0] dark:!text-white",
    disabled: "!bg-[#E5E1DF] !text-[#A09D9B] dark:!text-muted-foreground dark:!bg-[#4a4845]",
    default: "!bg-[#E3FF6A] !text-[#242424] dark:!text-foreground dark:!bg-[#485a2c]",
    secondary: "!bg-[#E5E1DF] !text-[#242424] dark:!text-foreground dark:!bg-[#4a4845]",
    destructive: "!bg-[#D90B0B] !text-[#FAF4F0] dark:!text-white",
    outline: "!border !border-[#E5E1DF] dark:!border-border !text-[#242424] dark:!text-foreground hover:!bg-[#F7F4F2] dark:hover:!bg-muted !bg-transparent",
};
var Badge = React.forwardRef(function (_a, ref) {
    var _b;
    var className = _a.className, variant = _a.variant, size = _a.size, disabled = _a.disabled, props = __rest(_a, ["className", "variant", "size", "disabled"]);
    var effectiveVariant = (_b = (disabled ? "disabled" : variant)) !== null && _b !== void 0 ? _b : "default";
    return (_jsx("div", __assign({ ref: ref, className: cn(badgeVariants({ variant: effectiveVariant, size: size }), disabled && "opacity-80", !disabled && "opacity-80 dark:opacity-100", className, 
        // Force lift-ui palette after consumer className (same pattern as Sheet/Dialog)
        badgeColorVariants[effectiveVariant]) }, props)));
});
Badge.displayName = "Badge";
export { Badge, badgeVariants };
